import _ from "lodash"

/**
 * Marks a nurse as edited in the newState.
 *
 * @param {string} nurseId - The id of the nurse to mark as edited
 * @param {Omit<keyof EditedNurseSchema, "nurseId">} editedField - The field to mark as edited
 * @param {ScheduleSchemaState} newState - The new state
 */
const markNurseAsEdited = (nurseId, editedField, newState) => {
	const existingEntry = newState.editedNurses.find(entry => entry.nurseId === nurseId)
	if (existingEntry) {
		existingEntry[editedField] = true
	} else {
		newState.editedNurses.push({
			nurseId,
			added: editedField === "added",
			deleted: editedField === "deleted",
			name: editedField === "name",
			scheduledStartTime: editedField === "scheduledStartTime",
			scheduledEndTime: editedField === "scheduledEndTime",
			scheduledLunchStartTime: editedField === "scheduledLunchStartTime",
			scheduledLunchEndTime: editedField === "scheduledLunchEndTime"
		})
	}
}

const markDiffsAsEditsNurses = (state, newState) => {
	for (const newNurse of newState.nurseSchedule) {
		const oldNurse = state.nurseSchedule.find(n => n.nurseId === newNurse.nurseId)
		if (!oldNurse) {
			markNurseAsEdited(newNurse.nurseId, "added", newState)
			continue
		}
		//look for differences in properties
		for (const field of Object.keys(newNurse)) {
			if (field === "transferredPatients") {
				continue
			}
			if (!_.isEqual(newNurse[field], oldNurse[field])) {
				markNurseAsEdited(newNurse.nurseId, field, newState)
			}
		}
	}
	//check for deleted nurses
	for (const oldNurse of state.nurseSchedule) {
		const newNurse = newState.nurseSchedule.find(n => n.nurseId === oldNurse.nurseId)
		if (!newNurse) {
			markNurseAsEdited(oldNurse.nurseId, "deleted", newState)
		}
	}
}

/**
 * Marks a patient as edited in the newState.
 *
 * @param {string} patientId - The id of the patient to mark as edited
 * @param {Omit<keyof EditedPatientSchema, "patientId">} editedField - The field to mark as edited
 * @param {ScheduleSchemaState} newState - The new state
 */
const markPatientAsEdited = (patientId, editedField, newState) => {
	const existingEntry = newState.editedPatients.find(entry => entry.patientId === patientId)
	if (existingEntry) {
		existingEntry[editedField] = true
	} else {
		newState.editedPatients.push({
			patientId,
			assignedNurse: editedField === "assignedNurse",
			assignedChair: editedField === "assignedChair",
			acuity: editedField === "acuity",
			pendingChanges: editedField === "pendingChanges",
			notes: editedField === "notes",
			cancellationConsideredForReassignment:
				editedField === "cancellationConsideredForReassignment",
			shownCancelledPatient: editedField === "shownCancelledPatient"
		})
	}
}

const markDiffsAsEditsPatients = (state, newState) => {
	for (const newPatient of newState.patientSchedule) {
		const oldPatient = state.patientSchedule.find(p => p.patientId === newPatient.patientId)
		if (!oldPatient) {
			markPatientAsEdited(newPatient.patientId, "added", newState)
			continue
		}
		//look for differences in properties
		for (const field of Object.keys(newPatient)) {
			if (
				field !== "assignedNurse" &&
				field !== "assignedChair" &&
				field !== "acuity" &&
				field !== "cancellationConsideredForReassignment" &&
				field !== "shownCancelledPatient"
			) {
				continue
			}

			if (!_.isEqual(newPatient[field], oldPatient[field])) {
				markPatientAsEdited(newPatient.patientId, field, newState)
			}
		}
	}
	//check for deleted patients
	for (const oldPatient of state.patientSchedule) {
		const newPatient = newState.patientSchedule.find(p => p.patientId === oldPatient.patientId)
		if (!newPatient) {
			markPatientAsEdited(oldPatient.patientId, "deleted", newState)
		}
	}
}

/**
 * Looks for differences between state, and newState.

 *
 * If any are found, it will update newState's edited properties acordingly.
 *
 * Edited properties are:
 * - editedNurses
 * - editedPatients
 *
 * @param {ScheduleSchemaState} state - The original state
 * @param {ScheduleSchemaState} newState - The new state
 * @returns {ScheduleSchemaState} The updated state with the differences marked as edits
 */
const markDiffsAsEdits = (state, newState) => {
	markDiffsAsEditsNurses(state, newState)
	markDiffsAsEditsPatients(state, newState)
}

export default markDiffsAsEdits
